import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
    TableContainer,
    Paper,
    Table,
    TableHead,
    TableRow,
    Tooltip,
    TableBody,
    Button,
    TableCell,
    Skeleton,
    TablePagination,
    CircularProgress,
    TableFooter,
    RadioGroup,
    FormControlLabel,
    Radio,
} from '@mui/material';
import CopyIcon from '@mui/icons-material/CopyAll';
import InfoIcon from '@mui/icons-material/Info';
import { Notification } from '../../common/Notification/Notification';
import {
    useAssignedPromotionsList,
    useFilteredList,
    useGetPrizesInfo,
    usePaginatedData,
} from './hooks/assignedPromotionsHooks';
import { configurationData, configWithPrizes, listAssignedPromosPropsType } from '../../types/componentTypes/assignedPromotions';
import { defaultNotifState } from '../../constants/currency-constants';
import { ComponentAccess } from '../../components/componentAccess/ComponentAccess';
import { rolesMap } from '../../constants/roles-constants';
import { useCountries } from '../../hooks/useCountries';
import { campaignData } from '../../types/componentTypes/assignPromo';
import { CopyPromotionModal } from '../../components/modals/CopyPromotionModal';
import { PrizeTableItem } from '../../types/componentTypes/PrizeTypes';

function ListAssignedPromotions(listAssignedPromosProps: listAssignedPromosPropsType & {
    data: campaignData;
    selectedPromoId?: string;
    isCopyMode: boolean;
    setIsCopyMode: Dispatch<SetStateAction<boolean>>;
    campaignConfigsData: configurationData[];
    setCampaignConfigsData?: Dispatch<SetStateAction<configurationData[]>>
}) {
    const { isCopyMode, setCampaignConfigsData, campaignConfigsData, data, setIsCopyMode } = listAssignedPromosProps;
    const [notificationState, setNotificationState] = useState(defaultNotifState);
    const [openCopyModal, setOpenCopyModal] = useState(false);
    const [selectedPromoId, setSelectedPromoId] = useState<string | null>(null);
    const [sourcePrizes, setSourcePrizes
    ] = useState<PrizeTableItem[]>();

    const { loading, s3PromotionsList } = useAssignedPromotionsList({ listAssignedPromosProps, setNotificationState })
    const { configPrizes, prizeLoading } = useGetPrizesInfo({ listAssignedPromosProps, setNotificationState, campaignConfigsData });
    const filteredPromotionsList = useFilteredList(campaignConfigsData, listAssignedPromosProps);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [page, setPage] = useState(0);
    const paginatedPromoList = usePaginatedData(filteredPromotionsList, rowsPerPage, page, setPage)
    const navigate = useNavigate();
    const { getCountryLabel } = useCountries();

    useEffect(() => {
        if (s3PromotionsList && s3PromotionsList?.length) {
            setCampaignConfigsData(s3PromotionsList);
        }

        // reassign data after copying promos
        if (campaignConfigsData.length && campaignConfigsData.length !== s3PromotionsList.length) {
            setCampaignConfigsData(campaignConfigsData);
        }
    }, [s3PromotionsList, campaignConfigsData, setCampaignConfigsData])

    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleOpenCopyPromo = (configPrizes: configWithPrizes) => {
        setOpenCopyModal(true);
        setSelectedPromoId(configPrizes.configurationId);
        setSourcePrizes(configPrizes.prizesArray);
    }

    const handleCloseCopyModal = () => {
        setSelectedPromoId(null);
        setSourcePrizes([]);
        setOpenCopyModal(false);
        setIsCopyMode(false);
    };

    const handleChangeRowsPerPage = async (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage(parseInt(event.target.value));
        setPage(0);
    };

    function PromotionName({ data, mechanic }: any) {
        if (mechanic === 'Collect & Get' || mechanic === 'Auto Collect & Get') {
            return (
                <div className="promotion-name">
                    <div className="circle">CG</div>
                    <span>{data}</span>
                </div>
            );
        } else if(mechanic === 'Prize Draw (Lottery)') {
            return (
                <div className="promotion-name">
                    <div className="circle">PD</div>
                    <span>{data}</span>
                </div>
            );
        } else if(mechanic === 'Lucky Numbers') {
            return (
                <div className="promotion-name">
                    <div className="circle">LN</div>
                    <span>{data}</span>
                </div>
            );
        } else {
            return (
                <div className="promotion-name">
                    <div className="circle">IW</div>
                    <span>{data}</span>
                </div>
            );
        }
    }

    const copyPromoId = (value: string) => {
        navigator.clipboard.writeText(value);
        setNotificationState({
            open: true,
            title: 'Promotion ID copied to clipboard',
            content: '',
            level: 'success',
        });
    };

    return (
        <>
            <Notification
                notificationState={notificationState}
                setNotificationState={setNotificationState}
            />
            <TableContainer component={Paper} className={'assign-promo-list-table'}>
                <Table className="tableClass" aria-label="customized table">
                    <TableHead>
                        <TableRow>
                            <TableCell className="rowCells">Promotion Name</TableCell>
                            <TableCell className="rowCells" align="left">
                                Promotion ID
                            </TableCell>
                            <TableCell className="rowCells" align="left">
                                Market
                            </TableCell>
                            <TableCell className="rowCells" align="left">
                                Start Date
                            </TableCell>
                            <TableCell className="rowCells" align="left">
                                End Date
                            </TableCell>
                            <TableCell className="rowCells" align="left">
                                Mechanic
                            </TableCell>
                            <TableCell
                                className="rowCells row-item-assign-promo"
                                align="left"
                            >
                                Prizes Added
                                <Tooltip
                                    title={
                                        <span>
                                            Information about whether the prizes are added or not for
                                            the specific promotion.
                                        </span>
                                    }
                                >
                                    <InfoIcon fontSize="small" />
                                </Tooltip>
                            </TableCell>
                        </TableRow>
                        {loading &&
                            Array(4)
                                .fill(1)
                                .map((el, i) => (
                                    <TableRow key={i}>
                                        {Array(8)
                                            .fill(1)
                                            .map((el, i) => (
                                                <TableCell key={i}>
                                                    <Skeleton />
                                                    <Skeleton width="60%" />
                                                </TableCell>
                                            ))}
                                    </TableRow>
                                ))}
                        {!loading && campaignConfigsData.length === 0 && (
                            <TableRow className="borderStyleFirstElement">
                                <TableCell
                                    align="left"
                                    colSpan={7}
                                    component="th"
                                    scope="row"
                                    className="tableFirstCell"
                                >
                                    + NEW PROMOTION
                                </TableCell>
                                <TableCell align="left" className="tableLastCell">
                                    <ComponentAccess minRole={rolesMap.PromotionManager}>
                                        <Button
                                            component={RouterLink}
                                            to={`/assignPromotion/${listAssignedPromosProps.data.promotion_id}`}
                                            variant="outlined"
                                            className={`buttonManageCreate ${listAssignedPromosProps.createDisabled ? 'disabled' : ''}`}
                                            disabled={listAssignedPromosProps.createDisabled}
                                        >
                                            Add
                                        </Button>
                                    </ComponentAccess>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableHead>
                    {!loading && paginatedPromoList.length > 0 && (
                        <TableBody>
                            {paginatedPromoList[page].map(
                                (assignedPromotion: any, i: number) => {
                                    const prizesAvailable =
                                        configPrizes.filter(
                                            (configPrizeObject) =>
                                                configPrizeObject.configurationId === assignedPromotion.promotionConfigurationId
                                        )[0]?.prizesArray?.length > 0
                                            ? true
                                            : false;
                                    const toEditPromotion = () => {
                                        navigate(
                                            `/editPromotion/${assignedPromotion.promotionConfigurationId}`
                                        );
                                    };
                                    return (
                                        <TableRow key={i} className={'borderStyleSecondElements'}>
                                            <TableCell data-testid={`promotion-row-${i}-name`}
                                                component="th"
                                                scope="row"
                                                className='tableFirstCellDisplayData'
                                            >
                                                {isCopyMode && (
                                                    <RadioGroup
                                                        sx={{
                                                            display: 'inline-block',
                                                        }}
                                                        aria-labelledby='copy-promo-radio-buttons-group'
                                                        name='copy-promo-radio-buttons-group'
                                                        value={selectedPromoId}
                                                    >
                                                        <FormControlLabel
                                                            onClick={() => {
                                                                const configWithPrizes = configPrizes.filter(item => item.configurationId === assignedPromotion.promotionConfigurationId)[0];
                                                                handleOpenCopyPromo(configWithPrizes);
                                                            }}
                                                            value={assignedPromotion.promotionConfigurationId}
                                                            control={<Radio />}
                                                            disabled={listAssignedPromosProps.createDisabled}
                                                            label=''
                                                        />
                                                    </RadioGroup>
                                                )}
                                                <PromotionName
                                                    data={assignedPromotion.promotionName}
                                                    mechanic={assignedPromotion.mechanic}
                                                />
                                            </TableCell>
                                            <TableCell
                                                align="left"
                                                className="tableMiddleCellDisplayData"
                                            >
                                                <div className="copy-icon-button-assign-promo">
                                                    {assignedPromotion.promotionConfigurationId}
                                                    <CopyIcon
                                                        fontSize="small"
                                                        onClick={() =>
                                                            copyPromoId(
                                                                assignedPromotion.promotionConfigurationId
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </TableCell>
                                            <TableCell
                                                align="left"
                                                className="tableMiddleCellDisplayData"
                                            >
                                                {getCountryLabel(assignedPromotion.market)}
                                            </TableCell>
                                            <TableCell
                                                align="left"
                                                className="tableMiddleCellDisplayData"
                                            >
                                                {assignedPromotion.startDate}
                                            </TableCell>
                                            <TableCell
                                                align="left"
                                                className="tableMiddleCellDisplayData"
                                            >
                                                {assignedPromotion.endDate}
                                            </TableCell>
                                            <TableCell
                                                align="left"
                                                className="tableMiddleCellDisplayData"
                                            >
                                                {assignedPromotion.mechanic}
                                            </TableCell>
                                            {!prizeLoading && (
                                                <TableCell
                                                    align="left"
                                                    className={
                                                        prizesAvailable
                                                            ? 'tableFirstCellDisplayDataGreen'
                                                            : 'tableFirstCellDisplayDataRed'
                                                    }
                                                >
                                                    {prizesAvailable ? 'Yes' : 'No'}
                                                </TableCell>
                                            )}
                                            {prizeLoading && (
                                                <TableCell
                                                    align="left"
                                                    className={'tableMiddleCellDisplayData'}
                                                >
                                                    <CircularProgress size={20} color="inherit" />
                                                </TableCell>
                                            )}
                                            <TableCell
                                                align="left"
                                                className="tableLastCellDisplayData"
                                            >
                                                <ComponentAccess minRole={rolesMap.ExportManager}>
                                                    <Button
                                                        onClick={toEditPromotion}
                                                        variant="outlined"
                                                        className={`buttonManageCreate ${listAssignedPromosProps.createDisabled ? 'disabled' : ''}`}
                                                        disabled={listAssignedPromosProps.createDisabled}
                                                    >
                                                        Manage
                                                    </Button>
                                                </ComponentAccess>
                                            </TableCell>
                                        </TableRow>
                                    );
                                }
                            )}
                        </TableBody>
                    )}
                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                className="paginationCell"
                                rowsPerPageOptions={[5, 10, 25]}
                                count={campaignConfigsData.length}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </TableRow>

                    </TableFooter>
                </Table>
            </TableContainer>
            <CopyPromotionModal
                open={openCopyModal}
                campaignConfigsData={campaignConfigsData}
                onClose={handleCloseCopyModal}
                campaignData={data}
                selectedPromoId={selectedPromoId}
                sourcePrizes={sourcePrizes}
                setNotificationState={setNotificationState}
                setCampaignConfigsData={setCampaignConfigsData}
            />
        </>
    );
}

export { ListAssignedPromotions };
